import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import {
  ACCESSIBILITY_LABELS,
  FULL_OF_DREAMS_ID,
  ParkCatalogIndex,
  offerOfProvider,
  packagesOf,
  variantPackages,
} from '../park-flow/park-catalog';

export interface IParkAccessChoice {
  key: string;        // 'standard' | índice de la variante accesible
  variant: any | null;
  offer: any | null;  // oferta del proveedor elegido (solo variantes accesibles)
}

interface IAccessCard {
  key: string;
  title: string;
  subtitle: string;
  variant: any | null;
  offer: any | null;
  price: number | null;
  currency: string;
}

/**
 * Paso "Accesibilidad": habitación estándar o una de las variantes accesibles del tipo de habitación,
 * limitadas al proveedor elegido. El catálogo trae una sola oferta por variante (la más barata); los paquetes con
 * tickets y comidas que tenga se reconstruyen con las habitaciones reales (variantPackages) para decir qué se puede agregar.
 */
@Component({
  selector: 'usb-park-access',
  templateUrl: './park-access.component.html',
  styleUrls: ['./park-access.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ParkAccessComponent implements OnChanges {
  @Input() roomType: any;
  @Input() rooms: any[];              // habitaciones reales de la respuesta (additionalInfo.rooms)
  @Input() hotelType = '';            // DISNEY | UNIVERSAL
  @Input() providerId: string;
  @Input() index: ParkCatalogIndex;
  @Input() standardOffer: any;        // oferta estándar más barata del proveedor (para el precio "desde")
  @Input() selectedKey = 'standard';
  @Input() currencies: any[];
  @Input() currencyAgency: any;
  @Input() buttonColorActive: string;
  @Input() clientSetting: any;

  @Output() choose = new EventEmitter<IParkAccessChoice>();

  cards: IAccessCard[] = [];

  ngOnChanges(): void {
    if (!this.roomType || !this.index) {
      this.cards = [];
      return;
    }
    const cards: IAccessCard[] = [];
    if (this.standardOffer) {
      cards.push({
        key: 'standard',
        title: 'Habitación Estándar',
        subtitle: ['Sin adaptaciones', this.addable(this.standardCodes())].filter(Boolean).join(' • '),
        variant: null,
        offer: this.standardOffer,
        price: this.index.price(this.standardOffer),
        currency: this.index.currency(this.standardOffer),
      });
    }
    (this.roomType.accessibility || []).forEach((variant: any, i: number) => {
      const offer = offerOfProvider(variant.offers, this.providerId);
      if (!offer || !this.index.real(offer)) {
        return;
      }
      cards.push({
        key: String(i),
        title: ACCESSIBILITY_LABELS[variant.kind] || variant.kind || 'Habitación accesible',
        subtitle: [variant.beds, this.describe(variant, offer)].filter(Boolean).join(' • '),
        variant,
        offer,
        price: this.index.price(offer),
        currency: this.index.currency(offer),
      });
    });
    this.cards = cards;
  }

  // Códigos de paquete de la habitación estándar que ofrece el proveedor.
  private standardCodes(): string[] {
    const packages = packagesOf(this.roomType);
    return Object.keys(packages).filter(code => !!offerOfProvider(packages[code], this.providerId));
  }

  // Qué se puede agregar a la variante según los paquetes que realmente tiene.
  private describe(variant: any, offer: any): string {
    const codes = Object.keys(variantPackages(offer, this.providerId, this.rooms || []));
    return this.addable(codes) || 'Se reserva solo la habitación, con la tarifa del proveedor';
  }

  // Tickets: paquete T* o, en Universal con Full of Dreams, add-on. Comidas: más de un plan disponible.
  private addable(codes: string[]): string {
    const addon = String(this.hotelType).toUpperCase() === 'UNIVERSAL' && this.providerId === FULL_OF_DREAMS_ID;
    const tickets = addon || codes.some(code => code.charAt(0) === 'T');
    const meals = new Set(codes.map(code => code.charAt(1))).size > 1;
    if (tickets && meals) {
      return 'Podrás agregar tickets y plan de comidas';
    }
    return tickets ? 'Podrás agregar tickets' : meals ? 'Podrás agregar plan de comidas' : '';
  }

  pick(card: IAccessCard): void {
    this.choose.emit({ key: card.key, variant: card.variant, offer: card.variant ? card.offer : null });
  }

  trackByKey(_: number, card: IAccessCard): string {
    return card.key;
  }
}
